import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Marker, MarkerClusterer, OverlayView } from "@react-google-maps/api";
import { Button } from "components/ui/button";
import { MAP_TYPES } from "pages/constants";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import pinNeutral from "static/media/h2h-pin-neutral.svg";
import pinActive from "static/media/h2h-pin-active.svg";
import pinAdded from "static/media/h2h-pin-added.svg";
import { useTargetingMapLoader } from "./map/loader";
import { MapFrame, useMapInstance } from "./map/MapFrame";
import { LoadingOverlay, ErrorOverlay, EmptyBanner } from "./map/MapStates";
import { MapToolbar } from "./map/MapToolbar";
import { MapSearch, type MapSearchSelection } from "./map/MapSearch";
import { ExpandMapModal } from "./ExpandMapModal";
import { useTargeting } from "./TargetingContext";
import { fetchH2hPins, filterPinsInZones, type H2hPin } from "./h2hLocations";

// Pins share one 24×28 art (design teardrop) rendered at a common size, tip
// anchored on the coordinate. Aspect preserved (31/36 ≈ 24/28).
const PIN = { w: 31, h: 36 };
const FOCUS_MS = 1500;

// Dark circular cluster badge (white count) matching the design (neutral/100,
// #222336) — built as a data URI so it needs no network/CDN asset (the default
// clusterer images are CDN-hosted and blocked).
const clusterCircle = (size: number) =>
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
      `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}" fill="#222336"/></svg>`
  );
const CLUSTER_STYLES = [40, 48, 56].map((size) => ({
  url: clusterCircle(size),
  width: size,
  height: size,
  textColor: "#FFFFFF",
  textSize: 13,
}));

// H2H distribution-point pins drawn inside the city's distribution zones. Lives
// inside MapFrame so it can reach the map instance + the shared pan helper.
// Clicking a recommended pin opens a tooltip (name + Add); Add commits the area,
// which fires the shared "New distribution area added" toast + View (AC21/AC23).
const H2hPinLayer = ({ pins }: { pins: H2hPin[] }) => {
  const { map, panTo } = useMapInstance();
  const { selectedAreas, addArea, highlightArea, mapFocusAreaId, clearMapFocus, setHoveredArea } = useTargeting();
  // The pin currently "active" (red) because it's being focused from its card.
  const [focusedPinId, setFocusedPinId] = useState<string | null>(null);
  const [activePin, setActivePin] = useState<H2hPin | null>(null);
  const focusTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(focusTimer.current), []);

  // Clicking the map background (not a pin) dismisses the tooltip.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", () => setActivePin(null));
    return () => listener.remove();
  }, [map]);

  // A list card asked to locate its pin: smoothly pan the map to it, mark it
  // active (red) and bounce it briefly, then revert to the added (green) state.
  // Coordinates come off the selected area itself, so it works even without the
  // pin set present.
  useEffect(() => {
    if (!mapFocusAreaId) return;
    const area = selectedAreas.find((a) => a.id === mapFocusAreaId);
    if (area && typeof area.lat === "number" && typeof area.lng === "number") {
      panTo({ lat: area.lat, lng: area.lng });
      setFocusedPinId(area.id);
      clearTimeout(focusTimer.current);
      focusTimer.current = setTimeout(() => setFocusedPinId(null), FOCUS_MS);
    }
    clearMapFocus();
  }, [mapFocusAreaId, selectedAreas, panTo, clearMapFocus]);

  const selectedIds = useMemo(() => new Set(selectedAreas.map((a) => a.id)), [selectedAreas]);

  // Marker icons need the Google geometry types; built once the script is loaded
  // (guaranteed inside MapFrame). Tip-anchored (bottom-centre) so the pin sits
  // on its coordinate. neutral = black, active = red (focused), added = green.
  const icons = useMemo(() => {
    if (!window.google) return null;
    const g = window.google.maps;
    const make = (url: string) => ({
      url,
      scaledSize: new g.Size(PIN.w, PIN.h),
      anchor: new g.Point(PIN.w / 2, PIN.h),
    });
    return { neutral: make(pinNeutral), active: make(pinActive), added: make(pinAdded) };
  }, []);

  const handleAdd = useCallback(() => {
    if (!activePin) return;
    addArea({
      id: activePin.id,
      value: activePin.name,
      lat: activePin.lat,
      lng: activePin.lng,
      type: MAP_TYPES.MARKER,
    });
    setActivePin(null);
  }, [activePin, addArea]);

  return (
    <>
      <MarkerClusterer averageCenter styles={CLUSTER_STYLES}>
        {(clusterer) => (
          <>
            {pins
              .filter((pin) => !selectedIds.has(pin.id))
              .map((pin) => (
                <Marker
                  key={pin.id}
                  position={{ lat: pin.lat, lng: pin.lng }}
                  title={pin.name}
                  icon={activePin?.id === pin.id ? icons?.active : icons?.neutral}
                  clusterer={clusterer}
                  onClick={() => setActivePin(pin)}
                />
              ))}
          </>
        )}
      </MarkerClusterer>
      {/* Selected pins are drawn from the selected areas (not the fetched set) so
          custom points created from the search — which aren't in the recommended
          catalogue — also render. Hover mirrors onto the list card (AC24); the
          pin stays green (already its selected colour). */}
      {selectedAreas
        .filter((area) => typeof area.lat === "number" && typeof area.lng === "number")
        .map((area) => (
          <Marker
            key={area.id}
            position={{ lat: area.lat as number, lng: area.lng as number }}
            title={area.value}
            icon={focusedPinId === area.id ? icons?.active : icons?.added}
            animation={focusedPinId === area.id ? google.maps.Animation.BOUNCE : undefined}
            onClick={() => highlightArea(area.id)}
            onMouseOver={() => setHoveredArea(area.id)}
            onMouseOut={() => setHoveredArea(null)}
          />
        ))}

      {activePin && (
        <OverlayView
          position={{ lat: activePin.lat, lng: activePin.lng }}
          mapPaneName={OverlayView.FLOAT_PANE}
          getPixelPositionOffset={() => ({ x: 0, y: 0 })}
        >
          {/* Positioned via CSS transform (not getPixelPositionOffset) so it
              doesn't jump on first paint before the card is measured: centred
              horizontally on the pin and lifted above its tip. */}
          <div
            style={{ transform: `translate(-50%, calc(-100% - ${PIN.h + 6}px))` }}
            className="flex w-[320px] items-center gap-5 rounded-lg border border-solid border-border bg-background p-4 shadow-[0px_10px_15px_rgba(0,0,0,0.15)]"
            data-cypress-id="targeting-pin-tooltip"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{activePin.name}</p>
              <p className="text-xs text-muted-foreground">Recommended for distribution</p>
            </div>
            <Button type="button" className="h-9 shrink-0 px-4" onClick={handleAdd} data-cypress-id="targeting-pin-add">
              Add
            </Button>
          </div>
        </OverlayView>
      )}
    </>
  );
};

// The in-map search for H2H (AC-S1/AC-S2): selecting an address pans the map to
// it and creates a new custom distribution point (pin) there — the "create new
// ones" path. Lives inside MapFrame so it can pan via the shared map instance.
const H2hMapSearch = () => {
  const { panTo } = useMapInstance();
  const { selectedCity, addArea } = useTargeting();
  const { country } = useClientConfig();

  const handleSelect = useCallback(
    ({ lat, lng, address }: MapSearchSelection) => {
      panTo({ lat, lng });
      addArea({
        id: `custom-${lat.toFixed(6)}-${lng.toFixed(6)}`,
        value: address,
        lat,
        lng,
        type: MAP_TYPES.MARKER,
        custom: true,
      });
    },
    [panTo, addArea]
  );

  return <MapSearch city={selectedCity} countryCode={country?.code ?? ""} onSelect={handleSelect} />;
};

// H2H distribution-point map (F4): selectable pins inside the city's
// distribution zones. Built on the shared MapFrame engine (loader, GoogleMap,
// pan, "area added" toast) so H2H and D2D share one map architecture. The
// `modal` variant is the same map inside the Expand-map modal — search but no
// nested expand control (AC26).
export const H2hMap = ({ variant = "inline" }: { variant?: "inline" | "modal" } = {}) => {
  const { selectedCity, recommendedZones } = useTargeting();
  // Shared singleton loader — gates the fetch so DZ filtering (Google geometry)
  // only runs once the script is ready.
  const { isLoaded } = useTargetingMapLoader();
  const isModal = variant === "modal";
  const [expandOpen, setExpandOpen] = useState(false);

  const cityId = selectedCity?.id;

  // The city's pin catalogue, cached per city so returning to Targeting after a
  // step switch is a cache hit — no refetch, no loading flash (PAD-31560). Pins
  // are deterministic per city and unaffected by area selection, so staleTime is
  // Infinity (the city change is what swaps the cache key). Trade-off by design:
  // there is no success-state refresh within a builder session (that would defeat
  // the no-refetch-on-return goal); picking a different city is the refresh path,
  // and the error state still offers Retry. DZ filtering against the recommended
  // zones happens at render below, not in the fetch, so the cached set re-filters
  // instantly without invalidating the query.
  const {
    data: fetchedPins = [],
    isLoading: pinsLoading,
    isError: pinsError,
    refetch: refetchPins,
  } = useQuery({
    queryKey: ["targeting-h2h-pins", cityId],
    queryFn: () => fetchH2hPins(cityId!),
    enabled: isLoaded && !!cityId,
    staleTime: Infinity,
  });
  // Zones can arrive from pathfinder before the Maps script is ready; DZ
  // filtering needs the geometry lib, so hold pins empty until the loader
  // settles (the memo re-runs on isLoaded, nothing is lost).
  const pins = useMemo(
    () => (isLoaded ? filterPinsInZones(fetchedPins, recommendedZones) : []),
    [isLoaded, fetchedPins, recommendedZones]
  );

  const center = useMemo(
    () => (selectedCity ? { lat: selectedCity.lat, lng: selectedCity.lng } : undefined),
    [selectedCity]
  );

  const overlays = (
    <>
      {pinsLoading && <LoadingOverlay label="Loading distribution points" />}
      {!pinsLoading && pinsError && (
        <ErrorOverlay message="Couldn't load distribution points." onRetry={() => void refetchPins()} />
      )}
      {!pinsLoading && !pinsError && pins.length === 0 && (
        <EmptyBanner message="No distribution areas available for this city." />
      )}
      <MapToolbar
        title={isModal ? undefined : `Distribution areas in ${selectedCity?.name ?? ""}`}
        expandable={!isModal}
        onExpand={() => setExpandOpen(true)}
      >
        <H2hMapSearch />
      </MapToolbar>
    </>
  );

  return (
    <>
      <MapFrame
        cypressId={isModal ? "targeting-map-h2h-modal" : "targeting-map-h2h"}
        center={center}
        overlays={overlays}
      >
        {!pinsLoading && !pinsError && <H2hPinLayer pins={pins} />}
      </MapFrame>
      {!isModal && (
        <ExpandMapModal open={expandOpen} onClose={() => setExpandOpen(false)} cityName={selectedCity?.name}>
          <H2hMap variant="modal" />
        </ExpandMapModal>
      )}
    </>
  );
};
